import { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import WarningIcon from '@mui/icons-material/Warning';
import { useTheme } from '@mui/material';
import { useAuth } from 'hooks/AuthContext';
import * as Types from 'types/types';
import { JobAnalysisProps, JobAnalysisScore, JobRequirement, SkillMatch } from './jobMatchTypes';
import {
  calculateScore,
  initialSkillMatches,
  overallMatchScore,
  requirementsFromJob,
} from './jobMatchUtils';

function useJobMatchAnalysis({ job, candidate, onAnalysisComplete }: JobAnalysisProps) {
  const { apiClient } = useAuth();
  const theme = useTheme();
  const [requirements, setRequirements] = useState<JobRequirement[]>([]);
  const [skillMatches, setSkillMatches] = useState<SkillMatch[]>([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [overallScore, setOverallScore] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [matchStatus, setMatchStatus] = useState('');
  const [percentage, setPercentage] = useState(0);
  const [analysis, setAnalysis] = useState<JobAnalysisScore | null>(null);
  const [startAnalysis, setStartAnalysis] = useState(true);
  const [firstRun, setFirstRun] = useState(true);
  const [reasoningIndex, setReasoningIndex] = useState<number | null>(null);

  const initializeRequirements = useCallback((): void => {
    if (!job?.requirements) return;
    const nextRequirements = requirementsFromJob(job);
    setRequirements(nextRequirements);
    setSkillMatches(initialSkillMatches(nextRequirements, candidate));
    setLoadingRequirements(false);
    setOverallScore(0);
  }, [candidate, job]);

  useEffect(() => {
    initializeRequirements();
  }, [initializeRequirements]);

  const skillMatchHandlers = useMemo(
    () => ({ onStatus: (status: Types.ChatMessageStatus): void => setMatchStatus(status.content) }),
    []
  );

  const applyMatch = useCallback((index: number, match: SkillMatch): void => {
    setSkillMatches(prev => {
      const updated = [...prev];
      updated[index] = match;
      setOverallScore(overallMatchScore(updated));
      return updated;
    });
  }, []);

  const fetchMatchData = useCallback(
    async (isFirstRun: boolean): Promise<void> => {
      const currentAnalysis = await apiClient.getJobAnalysis(job, candidate);
      for (let i = 0; i < requirements.length; i++) {
        try {
          const existingMatch = currentAnalysis?.skills.find(
            match => match.skill === requirements[i].requirement
          );
          if (existingMatch) {
            applyMatch(i, {
              ...existingMatch,
              status: 'complete',
              matchScore: calculateScore(existingMatch),
              domain: requirements[i].domain,
            });
          } else if (!isFirstRun) {
            await generateRequirementMatch(i, requirements[i].requirement, false);
          }
        } catch {
          setSkillMatches(prev => {
            const updated = [...prev];
            updated[i] = {
              ...updated[i],
              status: 'error',
              assessment: 'Failed to analyze this requirement.',
            };
            return updated;
          });
        }
        setPercentage(Math.round(((i + 1) / requirements.length) * 100));
      }
    },
    [apiClient, applyMatch, candidate, job, requirements]
  );

  const generateRequirementMatch = useCallback(
    async (index: number, skill: string, force: boolean): Promise<void> => {
      setSkillMatches(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          status: 'pending',
          assessment: '',
          evidenceDetails: [],
          evidenceStrength: 'none',
          matchScore: 0,
        };
        return updated;
      });
      const request = apiClient.candidateMatchForRequirement(
        candidate.id || '',
        skill,
        force,
        skillMatchHandlers
      );
      const result = await request.promise;
      setMatchStatus('');
      applyMatch(index, {
        ...result.skillAssessment,
        status: 'complete',
        matchScore: calculateScore(result.skillAssessment),
        domain: skillMatches[index]?.domain || '',
      });
    },
    [apiClient, applyMatch, candidate.id, skillMatchHandlers, skillMatches]
  );

  useEffect(() => {
    if (
      (!startAnalysis && !firstRun) ||
      analyzing ||
      !job.requirements ||
      requirements.length === 0
    )
      return;
    setAnalyzing(true);
    setPercentage(0);
    fetchMatchData(firstRun).then(() => {
      setAnalyzing(false);
      setStartAnalysis(false);
    });
    setFirstRun(false);
  }, [analyzing, fetchMatchData, firstRun, job.requirements, requirements, startAnalysis]);

  useEffect(() => {
    if (skillMatches.length === 0) return;
    const finished = skillMatches.every(
      match => match.status === 'complete' || match.status === 'error'
    );
    if (!finished || analysis?.score === overallScore) return;
    const nextAnalysis = { score: overallScore, skills: skillMatches };
    setAnalysis(nextAnalysis);
    onAnalysisComplete?.(nextAnalysis);
  }, [analysis, onAnalysisComplete, overallScore, skillMatches]);

  const getMatchColor = (score: number): string => {
    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.info.main;
    if (score >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getStatusIcon = (status: string, score: number): JSX.Element => {
    if (status === 'pending' || status === 'waiting') return <PendingIcon />;
    if (status === 'error') return <ErrorIcon color="error" />;
    if (score >= 70) return <CheckCircleIcon color="success" />;
    if (score >= 40) return <WarningIcon color="warning" />;
    return <ErrorIcon color="error" />;
  };

  return {
    analyzing,
    beginAnalysis: (): void => {
      initializeRequirements();
      setStartAnalysis(true);
    },
    expanded,
    getMatchColor,
    getStatusIcon,
    handleAccordionChange:
      (panel: string) =>
      (_: React.SyntheticEvent, isExpanded: boolean): void =>
        setExpanded(isExpanded ? panel : false),
    handleAssessmentRegenerate: generateRequirementMatch,
    loadingRequirements,
    matchStatus,
    overallScore,
    percentage,
    reasoningIndex,
    setReasoningIndex,
    skillMatches,
    startAnalysis,
  };
}

export { useJobMatchAnalysis };
